// === MFA challenge gate ===
//
// Blocks the app behind a 6-digit code whenever the session is authenticated but
// has not satisfied the second factor.
//
// WHY THIS SITS IN ProtectedRoute RATHER THAN THE LOGIN FORM
//   Password sign-in succeeds and produces a valid session at aal1 even when a
//   factor is enrolled — Supabase does not block it. So `user` is set, the login
//   page redirects, and the app loads against an API that will reject every
//   /admin call (verifySuperadmin requires aal2 once a verified factor exists).
//   Gating here catches every route into the app: a fresh sign-in, a restored
//   session from storage, and a session that was fine yesterday but had a factor
//   enrolled on another device since.
//
// The signal is Supabase's own: getAuthenticatorAssuranceLevel() reports
// currentLevel and nextLevel. nextLevel === 'aal2' means "this account has a
// verified factor"; if currentLevel has not caught up, the code is owed.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Alert, Box, Button, CircularProgress, CssBaseline, Paper, TextField, Typography,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { getSupabaseClient, useAuth } from '../lib/auth';

interface MfaChallengeGateProps {
  readonly children: ReactNode;
}

type GateState = 'checking' | 'satisfied' | 'challenge';

export function MfaChallengeGate({ children }: MfaChallengeGateProps) {
  const { user, signOut } = useAuth();
  const [state, setState] = useState<GateState>('checking');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!user) { setState('satisfied'); return; }
    try {
      const supabase = await getSupabaseClient();
      const { data, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalErr) throw aalErr;

      if (data?.nextLevel === 'aal2' && data.currentLevel !== 'aal2') {
        const { data: list } = await supabase.auth.mfa.listFactors();
        const totp = (list?.totp ?? []).find((f) => f.status === 'verified');
        if (!totp) {
          // nextLevel says aal2 but no usable factor — don't strand the user.
          setState('satisfied');
          return;
        }
        setFactorId(totp.id);
        setState('challenge');
        return;
      }
      setState('satisfied');
    } catch {
      // Never lock someone out because this check itself failed. The API is the
      // real enforcement point; this gate is the prompt that lets them satisfy it.
      setState('satisfied');
    }
  }, [user]);

  useEffect(() => { void check(); }, [check]);

  const submit = useCallback(async () => {
    if (!factorId || code.length < 6) return;
    setBusy(true);
    setError(null);
    try {
      const supabase = await getSupabaseClient();
      const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (verifyErr) throw verifyErr;
      setCode('');
      setState('satisfied');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  }, [factorId, code]);

  if (state === 'checking') return null;
  if (state === 'satisfied') return <>{children}</>;

  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a1929', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 380, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Two-factor required</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app to finish signing in.
          </Typography>

          {error !== null ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          <TextField
            autoFocus
            fullWidth
            size="small"
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            sx={{ mb: 2 }}
          />

          <Button fullWidth variant="contained" onClick={() => void submit()} disabled={busy || code.length < 6}>
            {busy ? <CircularProgress size={20} /> : 'Verify'}
          </Button>
          <Button fullWidth size="small" sx={{ mt: 1 }} onClick={() => void signOut()} disabled={busy}>
            Sign out
          </Button>

          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
            Lost your phone? Another superadmin can reset two-factor for your account from the Users page.
          </Typography>
        </Paper>
      </Box>
    </>
  );
}
